import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../components/primitives/layout/PageContainer";
import { PageHeader } from "../../components/primitives/layout/PageHeader";
import { usePopupMenu } from "../../components/primitives/navigation/PopupMenu";
import { RowTitleLink } from "../../components/primitives/data-display/RowTitleLink";
import { Pill } from "../../components/primitives/data-display/Pill";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../components/primitives/data-display/DataSequence";
import { LoadingState } from "../../components/primitives/feedback/LoadingState";
import { EmptyState } from "../../components/primitives/feedback/EmptyState";
import { VerdictBadge } from "../../components/domain/data-display/VerdictBadge";
import { useOpenState } from "../../hooks/useOpenState";
import { useLocaleFormatters } from "../../hooks/useLocaleFormatters";
import type { VerificationHistoryItem } from "../../types/claims";
import { feedbackPath, ROUTES } from "../../route_paths";
import { useAccountVerifications } from "./useAccountVerifications";

/** The account's "My verifications" section: what the person asked, with the result they saw. */
export function AccountVerifications() {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormatters();
  const { items, loading, error, pagination } = useAccountVerifications();
  const navigate = useNavigate();
  const copied = useOpenState();

  function copyId(id: string) {
    navigator.clipboard.writeText(id).catch(() => {});
    copied.setOpen();
  }

  const columns: DataSequenceColumn<VerificationHistoryItem>[] = [
    {
      key: "claim",
      header: t("account.verifications.claim"),
      cell: (r) => (
        <RowTitleLink to={ROUTES.verify.path({ userEventVerificationId: r.id })}>
          {r.claim_text}
        </RowTitleLink>
      ),
      cardRole: "primary",
    },
    {
      key: "result",
      header: t("account.verifications.result"),
      cell: (r) => <VerificationResult verdict={r.verdict} />,
    },
    {
      key: "asked",
      header: t("account.verifications.asked"),
      cell: (r) => formatDate(r.asked_at),
    },
    {
      key: "copyId",
      header: "",
      cardRole: "action",
      cell: (r) => (
        <RowActions
          onCopyId={() => copyId(r.id)}
          onSendFeedback={() => navigate(feedbackPath({ verification: r.id }))}
        />
      ),
    },
  ];

  return (
    <PageContainer embedded>
      <PageHeader
        size="compact"
        title={t("account.verifications.title")}
        subtitle={t("account.verifications.subtitle")}
      />
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? (
        <LoadingState />
      ) : (
        <DataSequence
          columns={columns}
          rows={items}
          getRowId={(r) => r.id}
          pagination={pagination}
          emptyMessage={
            <EmptyState
              message={t("account.verifications.empty")}
              action={
                <Button component={Link} to={ROUTES.landing.path()} variant="contained">
                  {t("account.verifications.action")}
                </Button>
              }
            />
          }
        />
      )}
      <Snackbar
        open={copied.open}
        onClose={copied.setClose}
        autoHideDuration={2500}
        message={t("account.verifications.idCopied")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </PageContainer>
  );
}

/** The result a person was shown: its verdict, or why there is none. */
function VerificationResult({ verdict }: { verdict: VerificationHistoryItem["verdict"] }) {
  const { t } = useTranslation();
  if (verdict === null) return <Pill label={t("account.verifications.notChecked")} />;
  if (verdict === "NO_COVERAGE") return <Pill label={t("account.verifications.notFound")} />;
  return <VerdictBadge verdict={verdict} size="compact" />;
}

/** What can be done with a verification from its row: copy its id, or send feedback about it. */
function RowActions({
  onCopyId,
  onSendFeedback,
}: {
  onCopyId: () => void;
  onSendFeedback: () => void;
}) {
  const { t } = useTranslation();
  const { menu, setPopupMenuAnchor } = usePopupMenu([
    [
      { label: t("account.verifications.copyId"), onClick: onCopyId },
      { label: t("account.verifications.sendFeedback"), onClick: onSendFeedback },
    ],
  ]);
  return (
    <>
      <IconButton
        size="small"
        onClick={setPopupMenuAnchor}
        aria-label={t("account.verifications.actions")}
      >
        <MoreVertIcon />
      </IconButton>
      {menu}
    </>
  );
}
